/**
 * How a stored answer turn reads in the AI panel (R1 UX1).
 *
 * When someone answers one of the AI's questions in the panel, the backend
 * stores the turn's user message as the line the model reads (backend
 * `renderAnswerMessage`, lib/questions/turn-answer.ts):
 *
 *   Answer to your question "<question>": <answer>
 *
 * optionally followed by a line about updating the draft, then anything else
 * the person wrote. Live, the bubble shows only the answer (or "Not known
 * yet"). After a reload the bubble is rebuilt from the stored message, so this
 * turns that line back into what was shown live. The format is built by our
 * own code; keep the strings here in step with `renderAnswerMessage`.
 *
 * Known limit: a question that itself contains `": ` splits at that point.
 */

import { QUESTION_NOT_KNOWN } from '@/lib/question-copy'

const ANSWER_PREFIX = 'Answer to your question "'
const QUESTION_END = '": '
const NOT_KNOWN_LINE = 'not known yet.'

/** The lines `renderAnswerMessage` adds for the model about the draft; never shown live. */
const DRAFT_UPDATE_LINES: ReadonlySet<string> = new Set([
  'The draft is being updated with this answer in the background.',
  'The draft will be updated with this answer when the current drafting finishes.',
  "Updating the draft from this answer isn't available yet, so the draft was not changed.",
])

/** The text a stored user message shows in its bubble: the answer for an answer turn, else the message. */
export function displayTurnUserMessage(message: string): string {
  if (!message.startsWith(ANSWER_PREFIX)) return message
  const questionEnd = message.indexOf(QUESTION_END, ANSWER_PREFIX.length)
  if (questionEnd === -1) return message
  const rest = message.slice(questionEnd + QUESTION_END.length)
  if (rest === NOT_KNOWN_LINE) return QUESTION_NOT_KNOWN
  const shown = rest
    .split('\n\n')
    .filter((part) => !DRAFT_UPDATE_LINES.has(part))
    .join('\n\n')
    .trim()
  return shown.length > 0 ? shown : message
}
